import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Trash2, TriangleAlert } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  createTabNudge,
  updateTabNudge,
  getTabNudge,
  deleteTabNudge,
  listTabNudges,
  listTabNudgeTargets,
} from '../services/tabNudgeService';
import { TabNudgeTarget } from '../types/TabNudge';
import { DashTenant, tenantDisplayName } from '../types/Show';
import { DateTimePicker } from '../components/DateTimePicker';

export default function TabNudgeForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Nudge fields
  const [tenant, setTenant] = useState<DashTenant | ''>('');
  const [targetId, setTargetId] = useState('');
  const [startMs, setStartMs] = useState<number | null>(null);
  const [endMs, setEndMs] = useState<number | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [originalEndAt, setOriginalEndAt] = useState<number | null>(null);

  // Nudgeable tabs from the live layouts
  const [targets, setTargets] = useState<TabNudgeTarget[]>([]);
  const [targetsLoading, setTargetsLoading] = useState(true);

  // Pre-save overlap warning (non-blocking)
  const [liveOverlapCount, setLiveOverlapCount] = useState(0);

  useEffect(() => {
    listTabNudgeTargets()
      .then(response => setTargets(response.targets))
      .catch(() => toast.error('Failed to load nudgeable tabs'))
      .finally(() => setTargetsLoading(false));
  }, []);

  // Load existing nudge in edit mode. Timestamps arrive as ISO-8601 strings;
  useEffect(() => {
    if (!isEdit || !id) return;
    (async () => {
      try {
        const nudge = await getTabNudge(id);
        setTenant(nudge.tenant);
        setTargetId(nudge.targetId);
        setStartMs(nudge.startAt);
        setEndMs(nudge.endAt);
        setOriginalEndAt(nudge.endAt);
        setEnabled(nudge.enabled);
      } catch {
        toast.error('Failed to load tab nudge');
        navigate('/tab-nudges');
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, id, navigate]);

  // Warn before save when the chosen tab already has a live nudge for the tenant
  useEffect(() => {
    if (!tenant || !targetId) {
      setLiveOverlapCount(0);
      return;
    }
    listTabNudges({ tenant, targetId })
      .then(response =>
        setLiveOverlapCount(response.items.filter(n => n.liveNow && n.id !== id).length)
      )
      .catch(() => setLiveOverlapCount(0));
  }, [tenant, targetId, id]);

  const selectedTarget = useMemo(
    () => targets.find(t => t.targetId === targetId) ?? null,
    [targets, targetId]
  );

  const selectedScreens = useMemo(
    () => (selectedTarget ? Array.from(new Set(selectedTarget.screens)) : []),
    [selectedTarget]
  );

  const validate = (): string | null => {
    if (!tenant) return 'Please select a tenant';
    if (!targetId) return 'Please select a tab';
    if (isEdit && startMs === null) return 'Please set a start time';
    if (startMs !== null && endMs !== null && endMs <= startMs) {
      return 'End time must be after start time';
    }
    if (isEdit && originalEndAt !== null && endMs === null) {
      return 'End time cannot be cleared on an existing nudge — delete and recreate for open-ended';
    }
    return null;
  };

  const handleSave = async () => {
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      const startAt = startMs;
      const endAt = endMs;

      let response;
      if (isEdit && id) {
        response = await updateTabNudge(id, {
          ...(startAt !== null && { startAt }),
          ...(endAt !== null && { endAt }),
          enabled,
        });
        toast.success('Tab nudge updated');
      } else {
        response = await createTabNudge({
          tenant: tenant as DashTenant,
          targetId,
          startAt,
          endAt,
          enabled,
        });
        toast.success('Tab nudge created');
      }
      if (response.overlappingActiveNudgeIds.length > 0) {
        toast.warning(
          `Overlaps ${response.overlappingActiveNudgeIds.length} active nudge${
            response.overlappingActiveNudgeIds.length === 1 ? '' : 's'
          } on this tab`
        );
      }
      navigate('/tab-nudges');
    } catch {
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} tab nudge`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !window.confirm('Delete this tab nudge? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await deleteTabNudge(id);
      toast.success('Tab nudge deleted');
      navigate('/tab-nudges');
    } catch {
      toast.error('Failed to delete tab nudge');
      setDeleting(false);
    }
  };

  const windowError =
    startMs !== null && endMs !== null && endMs <= startMs
      ? 'End time must be after start time'
      : null;

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="flex items-center gap-3 mb-8">
        <Button variant="ghost" size="icon" onClick={() => navigate('/tab-nudges')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold">
          {isEdit ? 'Edit Tab Nudge' : 'New Tab Nudge'}
        </h1>
      </div>

      <div className="space-y-6">
        {/* Tenant */}
        <div className="space-y-2">
          <Label>Tenant</Label>
          {isEdit ? (
            <Input value={tenantDisplayName[tenant as DashTenant]} disabled />
          ) : (
            <Select value={tenant} onValueChange={(value: DashTenant) => setTenant(value)}>
              <SelectTrigger>
                <SelectValue placeholder="Select tenant" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="REELS">{tenantDisplayName.REELS}</SelectItem>
                <SelectItem value="SHORTFREE">{tenantDisplayName.SHORTFREE}</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Tab */}
        <div className="space-y-2">
          <Label>Tab</Label>
          {isEdit ? (
            <div className="rounded-md border bg-secondary/20 px-3 py-2">
              <div className="font-medium">{selectedTarget?.displayName ?? targetId}</div>
              <div className="text-xs text-muted-foreground">{targetId}</div>
            </div>
          ) : targetsLoading ? (
            <p className="text-sm text-muted-foreground">Loading tabs…</p>
          ) : targets.length === 0 ? (
            <p className="text-sm text-muted-foreground">No nudgeable tabs found in the live layouts.</p>
          ) : (
            <Select value={targetId} onValueChange={setTargetId}>
              <SelectTrigger className="h-auto">
                <SelectValue placeholder="Select tab" />
              </SelectTrigger>
              <SelectContent>
                {targets.map(target => (
                  <SelectItem key={target.targetId} value={target.targetId}>
                    <span className="font-medium">{target.displayName}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{target.targetId}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {selectedScreens.length > 0 && (
            <p className="text-xs text-muted-foreground">
              Appears on: {selectedScreens.join(', ')}
            </p>
          )}
        </div>

        {/* Overlap warning (non-blocking) */}
        {liveOverlapCount > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/50 bg-amber-500/10 px-3 py-2 text-sm text-amber-600">
            <TriangleAlert className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              This tab already has {liveOverlapCount} live nudge{liveOverlapCount === 1 ? '' : 's'} for{' '}
              {tenantDisplayName[tenant as DashTenant]}. Saving another is allowed but usually unnecessary.
            </span>
          </div>
        )}

        {/* Window */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Start time (optional)</Label>
            <DateTimePicker
              value={startMs}
              onChange={setStartMs}
              placeholder="Starts immediately"
              disabled={d => d.getTime() < new Date().setHours(0, 0, 0, 0)}
            />
            <p className="text-xs text-muted-foreground">Leave empty to start immediately.</p>
          </div>
          <div className="space-y-2">
            <Label>End time (optional)</Label>
            <DateTimePicker
              value={endMs}
              onChange={setEndMs}
              placeholder="Open-ended"
              disabled={d => d.getTime() < new Date(Math.max(startMs ?? Date.now(), Date.now())).setHours(0, 0, 0, 0)}
            />
            {windowError ? (
              <p className="text-xs text-destructive">{windowError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">Leave empty for open-ended (until disabled).</p>
            )}
          </div>
          <p className="text-xs text-muted-foreground sm:col-span-2">
            Times are in your local timezone ({Intl.DateTimeFormat().resolvedOptions().timeZone}, UTC{-(new Date().getTimezoneOffset() / 60) >= 0 ? '+' : ''}{-(new Date().getTimezoneOffset() / 60)}).
          </p>
        </div>

        {/* Enabled */}
        <div className="flex items-center justify-between rounded-md border px-3 py-3">
          <div>
            <Label className="font-medium">Enabled</Label>
            <p className="text-xs text-muted-foreground">Disabled nudges never show, regardless of window.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center pt-2">
          <div>
            {isEdit && (
              <Button
                variant="destructive"
                onClick={handleDelete}
                disabled={deleting || saving}
              >
                {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/tab-nudges')} disabled={saving || deleting}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || deleting || windowError !== null}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {isEdit ? 'Save Changes' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
